// improvements-client.ts — shared client for the cross-surface Suggestions &
// Bounties "Improve" floater (docs/suggestions-bounties-prd.md).
//
// There is no single global user in this app: each surface authenticates with
// its own token under a distinct header. The floater is mounted once, globally,
// so it must figure out WHICH identity the current visitor is from the URL +
// the matching localStorage token, then talk to the API with the right header.
// This mirrors the server-side resolveImprovementIdentity order.

const BASE = import.meta.env.BASE_URL;

export type SurfaceKind = "admin" | "founder" | "investor" | "advisor" | "mentor";

export interface SurfaceConfig {
  kind: SurfaceKind;
  /** localStorage key holding this identity's auth token. */
  tokenKey: string;
  /** request header the server reads this identity's token from. */
  header: string;
  /** default suggestion `type` for this surface. */
  defaultType: string;
  /** human label shown in the floater header ("Suggesting as Founder"). */
  label: string;
}

const SURFACES: Record<SurfaceKind, SurfaceConfig> = {
  admin: { kind: "admin", tokenKey: "admin_token", header: "x-admin-token", defaultType: "platform_internal", label: "Admin" },
  founder: { kind: "founder", tokenKey: "founder_token", header: "x-founder-token", defaultType: "founder_experience", label: "Founder" },
  investor: { kind: "investor", tokenKey: "investor_token", header: "x-investor-token", defaultType: "investor_experience", label: "Investor" },
  advisor: { kind: "advisor", tokenKey: "banker_token", header: "x-banker-token", defaultType: "advisor_experience", label: "Advisor" },
  mentor: { kind: "mentor", tokenKey: "mentor_token", header: "x-mentor-token", defaultType: "mentor_experience", label: "Mentor" },
};

function stripBase(pathname: string): string {
  const base = BASE.replace(/\/$/, "");
  if (base && pathname.startsWith(base)) return pathname.slice(base.length) || "/";
  return pathname;
}

/**
 * Decide which identity the current page belongs to, based on the URL. Returns
 * null when the surface is public/unknown OR when the matching token is absent
 * (so the floater stays hidden for logged-out visitors). Surface is chosen by
 * path so an admin browsing a founder portal submits as the identity they are
 * actually logged in as there.
 */
export function detectSurface(pathname: string = window.location.pathname): SurfaceConfig | null {
  const p = stripBase(pathname);
  let cfg: SurfaceConfig | null = null;
  if (p === "/admin" || p.startsWith("/admin/") || p.startsWith("/c/")) cfg = SURFACES.admin;
  else if (p.startsWith("/banker")) cfg = SURFACES.advisor;
  else if (p.startsWith("/investor")) cfg = SURFACES.investor;
  else if (p.startsWith("/mentor")) cfg = SURFACES.mentor;
  else if (/^\/[a-z0-9][a-z0-9-]*\/founder(\/|$)/.test(p)) cfg = SURFACES.founder;
  if (!cfg) return null;
  if (!localStorage.getItem(cfg.tokenKey)) return null;
  return cfg;
}

/** Theme scope for useThemeMode, derived from the surface. */
export function themeScopeFor(kind: SurfaceKind): "admin" | "founder" | "investor" | "banker" | "mentor" {
  return kind === "advisor" ? "banker" : kind;
}

function headers(s: SurfaceConfig): Record<string, string> {
  return { "Content-Type": "application/json", [s.header]: localStorage.getItem(s.tokenKey) || "" };
}

export const SUB_CATEGORIES = ["bug", "ux", "feature", "content", "performance", "trust_safety", "other"] as const;
export type SubCategory = (typeof SUB_CATEGORIES)[number];

export interface SubmitInput {
  title: string;
  bodyMd: string;
  subCategory: SubCategory;
  type?: string;
  surfacePath?: string | null;
}

export interface ApiResult<T> {
  ok: boolean;
  status: number;
  data?: T;
  error?: string;
}

async function call<T>(s: SurfaceConfig, path: string, init?: RequestInit): Promise<ApiResult<T>> {
  try {
    const r = await fetch(`${BASE}api${path}`, { ...init, headers: { ...headers(s), ...(init?.headers || {}) } });
    let body: any = null;
    try { body = await r.json(); } catch { /* empty body */ }
    if (!r.ok) {
      return { ok: false, status: r.status, error: body?.error || `Request failed (${r.status}).` };
    }
    return { ok: true, status: r.status, data: body as T };
  } catch {
    return { ok: false, status: 0, error: "Network error. Check your connection and try again." };
  }
}

export interface Suggestion {
  id: number;
  type: string;
  subCategory: string;
  title: string;
  bodyMd: string;
  status: string;
  surfacePath: string | null;
  createdAt: string;
  reviewNote?: string | null;
}

export interface BountySummary { balance: number; lifetimeEarned: number; lifetimeSpent: number; rank: number | null; totalEarners?: number }
export interface LedgerEntry { id: number; delta: number; reason: string; note: string | null; createdAt: string }
export interface RedemptionOffer {
  id: number; title: string; description: string | null; imageUrl: string | null;
  pointCost: number; stock: number | null; status: string; opensAt: string | null; closesAt: string | null;
}
export interface RedemptionRequest {
  id: number; offerId: number; pointCostSnapshot: number; status: string;
  adminNote: string | null; createdAt: string; resolvedAt: string | null;
  offerTitle?: string | null; offerImageUrl?: string | null;
}

export function submitSuggestion(s: SurfaceConfig, input: SubmitInput) {
  return call<{ suggestion: Suggestion }>(s, "/suggestions", { method: "POST", body: JSON.stringify(input) });
}
export function getMySuggestions(s: SurfaceConfig) {
  return call<{ suggestions: Suggestion[] }>(s, "/suggestions/mine");
}
export function getMyBounties(s: SurfaceConfig) {
  return call<{ identity: { kind: string; email: string; name: string | null }; summary: BountySummary; ledger: LedgerEntry[] }>(s, "/bounties/me");
}
export function getRedemptionOffers(s: SurfaceConfig) {
  return call<{ offers: RedemptionOffer[]; balance: number }>(s, "/redemption-offers");
}
export function redeemOffer(s: SurfaceConfig, offerId: number) {
  return call<{ request: RedemptionRequest; balance: number }>(s, "/redemption-requests", { method: "POST", body: JSON.stringify({ offerId }) });
}
export function getMyRedemptions(s: SurfaceConfig) {
  return call<{ requests: RedemptionRequest[] }>(s, "/redemption-requests/mine");
}
